import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useClientContext } from 'client/context';
import { Condition } from 'client/voladeum/conditions/types';
import { Indicator } from 'client/voladeum/indicators/types';
import { CreateStrategy, UpdateStrategy } from 'client/voladeum/strategies/types';
import { setAdapters } from 'ducks/adapters/slices';
import { setContractTypes } from 'ducks/contract-types/slices';
import { setExpiryMethods } from 'ducks/expiry-methods/slices';
import { setIndicators } from 'ducks/indicators/slices';
import { clearModal, updateModal } from 'ducks/modals/slices';
import { setOperators } from 'ducks/operators/slices';
import { setPairs } from 'ducks/pairs/slices';
import { updateSettings } from 'ducks/settings/slices';
import { setSignal, updateSignal } from 'ducks/signal/slices';
import { setSizingMethods } from 'ducks/sizing-methods/slices';
import { setStatuses } from 'ducks/statuses/slices';
import { selectStrategy } from 'ducks/strategy/selectors';
import { setStrategy } from 'ducks/strategy/slices';
import { setStrikeMethods } from 'ducks/strike-methods/slices';
import { useStrategyContext } from 'pages/context/StrategyContext';
import { PRIVATE } from 'router/routes';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import { useConditionLookups } from 'components/modals/conditions/hooks';

const useStrategyApi = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { strategy_id = '' } = useParams();

  const voladeum_strategy = useAppSelector(selectStrategy);
  const { strategy, setCondition } = useStrategyContext();
  const { Voladeum, handleError } = useClientContext();
  const conditionLookups = useConditionLookups();

  const getStrategyPage = useCallback(async () => {
    try {
      dispatch(updateSettings({ loading: true, loading_component: true }));

      const voladeum_strategy = await Voladeum.strategies.fetch(strategy_id);
      const [voladeum_signal, voladeum_indicators, voladeum_operators] = await Promise.all([
        Voladeum.signal.strategy(voladeum_strategy.data.strategy_id),
        Voladeum.indicators.list(),
        Voladeum.operators.list(),
      ]);

      dispatch(setStrategy(voladeum_strategy.data));
      dispatch(setSignal(voladeum_signal.data));
      dispatch(setIndicators(voladeum_indicators.data));
      dispatch(setOperators(voladeum_operators.data));
      dispatch(updateSettings({ loading: false, loading_component: false }));
    } catch (err) {
      handleError(err);
    }
  }, [dispatch, handleError, strategy_id, Voladeum]);

  const onStrategySubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      try {
        e.preventDefault();
        dispatch(updateSettings({ loading: true }));

        if (strategy.strategy_id) {
          const payload: UpdateStrategy = {
            strategy_name: strategy.strategy_name,
            contract_type_id: strategy.contract_type_id,
            sizing_method_id: strategy.sizing_method_id,
            max_position_pct: strategy.max_position_pct || null,
            max_daily_loss_pct: strategy.max_daily_loss_pct || null,
            max_drawdown_pct: strategy.max_drawdown_pct || null,
            strike_method_id: strategy.strike_method_id,
            strike_value: strategy.strike_value,
            expiry_method_id: strategy.expiry_method_id,
            expiry_value: strategy.expiry_value,
            stop_loss_offset_pct: strategy.stop_loss_offset_pct || null,
            take_profit_offset_pct: strategy.take_profit_offset_pct || null,
            take_profit_trail_pct: strategy.take_profit_trail_pct || null,
            slippage_tolerance_pct: strategy.slippage_tolerance_pct || null,
            activation_threshold: strategy.activation_threshold || null,
          };

          await Voladeum.strategies.update(strategy.strategy_id, payload);
        } else {
          const payload: CreateStrategy = {
            strategy_name: strategy.strategy_name,
            pair_id: strategy.pair_id,
            sizing_method_id: strategy.sizing_method_id,
            max_position: strategy.max_position_pct,
            max_daily_loss: strategy.max_daily_loss_pct,
            since_milliseconds: strategy.since_milliseconds,
            to_milliseconds: strategy.to_milliseconds,
            activation_threshold: strategy.activation_threshold,
          };
          const { data: strategy_id } = await Voladeum.strategies.create(payload);
          navigate(`${PRIVATE.STRATEGIES.path}/${strategy_id}`);
        }

        setStrategy(voladeum_strategy);
        dispatch(clearModal('strategy_manage'));
      } catch (err) {
        handleError(err);
      }
    },
    [dispatch, strategy, voladeum_strategy, Voladeum.strategies, navigate, handleError]
  );

  const getStrategyLookups = useCallback(async () => {
    try {
      dispatch(updateSettings({ loading: true }));
      const [
        voladeum_pairs,
        voladeum_status,
        voladeum_adapters,
        voladeum_sizing_methods,
        voladeum_expiry_methods,
        voladeum_strike_methods,
        voladeum_contract_types,
      ] = await Promise.all([
        Voladeum.pairs.list(),
        Voladeum.statuses.list(),
        Voladeum.adapters.list(),
        Voladeum.sizingMethods.list(),
        Voladeum.expiryMethods.list(),
        Voladeum.strikeMethods.list(),
        Voladeum.contractTypes.list(),
      ]);

      dispatch(setPairs(voladeum_pairs.data));
      dispatch(setStatuses(voladeum_status.data));
      dispatch(setAdapters(voladeum_adapters.data));
      dispatch(setSizingMethods(voladeum_sizing_methods.data));
      dispatch(setExpiryMethods(voladeum_expiry_methods.data));
      dispatch(setStrikeMethods(voladeum_strike_methods.data));
      dispatch(setContractTypes(voladeum_contract_types.data));
      dispatch(updateSettings({ loading: false }));
    } catch (err) {
      handleError(err);
    }
  }, [Voladeum, handleError, dispatch]);

  const getStrategyFilters = useCallback(async () => {
    try {
      dispatch(updateSettings({ loading: true }));
      const [voladeum_pairs, voladeum_status, voladeum_adapters] = await Promise.all([
        Voladeum.pairs.list(),
        Voladeum.statuses.list(),
        Voladeum.adapters.list(),
      ]);

      dispatch(setPairs(voladeum_pairs.data));
      dispatch(setStatuses(voladeum_status.data));
      dispatch(setAdapters(voladeum_adapters.data));
      dispatch(updateSettings({ loading: false }));
    } catch (err) {
      handleError(err);
    }
  }, [Voladeum, handleError, dispatch]);

  const getStrategyIndicatorSignal = useCallback(
    async (strategy_id: number, indicator: Indicator) => {
      try {
        dispatch(updateSettings({ loading_component: true }));
        const signal = await Voladeum.signal.strategyIndicator(strategy_id, indicator);
        dispatch(updateSignal(signal.data));
        dispatch(updateSettings({ loading_component: false }));
      } catch (err) {
        handleError(err);
      }
    },
    [Voladeum.signal, dispatch, handleError]
  );

  const manageStrategyTriggerCondition = useCallback(
    async (condition: Condition, data: { is_open: boolean; is_update: boolean }) => {
      try {
        dispatch(updateSettings({ loading: true }));
        await conditionLookups();
        setCondition(condition);

        dispatch(updateModal({ key: 'condition_manage', data }));
        dispatch(updateSettings({ loading: false }));
      } catch (err) {
        handleError(err);
      }
    },
    [conditionLookups, dispatch, handleError, setCondition]
  );

  return {
    onStrategySubmit,
    getStrategyPage,
    getStrategyFilters,
    getStrategyLookups,
    getStrategyIndicatorSignal,
    manageStrategyTriggerCondition,
  };
};

export { useStrategyApi };
